import { type FormEvent, useState } from 'react';

import { Logo } from '@/components/brand/Logo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ApiError, api } from '@/lib/api';
import { useApi } from '@/lib/useApi';

const MIN_PASSWORD_LENGTH = 12;

interface InvitationInfo {
  email: string;
  name: string;
  tenant: { name: string };
}

/**
 * 招待の受諾。
 *
 * 未ログインで開く唯一の画面。ここでパスワードを決めるとそのまま
 * ログイン済みになるので、招待された人は 1 画面で使い始められる。
 */
export function AcceptInvitationPage({
  token,
  onAccepted,
}: {
  token: string;
  onAccepted: () => void;
}) {
  const { data, error, loading } = useApi<InvitationInfo>(
    `/api/auth/invitation?token=${encodeURIComponent(token)}`,
  );
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setFailure(null);
    try {
      await api.post('/api/auth/invitation/accept', { token, password });
      onAccepted();
    } catch (cause) {
      setFailure(cause instanceof ApiError ? cause.message : '通信に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  const mismatch = confirm !== '' && confirm !== password;
  const ready = password.length >= MIN_PASSWORD_LENGTH && !mismatch && confirm !== '';

  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas px-6">
      <div className="flex w-full max-w-96 flex-col gap-6 border border-line bg-surface-raised px-8 py-10">
        <Logo height={30} className="text-brand-500" />

        {loading ? <p className="text-sm text-ink-subtle">読み込み中…</p> : null}

        {error ? (
          <p className="text-sm text-alert">
            この招待は使えません。期限が切れているか、すでに受諾されています。
            管理者に再発行を依頼してください。
          </p>
        ) : null}

        {data ? (
          <form onSubmit={submit} className="flex flex-col gap-6">
            <div className="flex flex-col gap-1">
              <span className="text-sm font-bold">{data.tenant.name} への招待</span>
              <span className="font-mono text-xs text-ink-subtle">{data.email}</span>
            </div>

            <label className="flex flex-col gap-2 text-sm">
              パスワードを設定
              <Input
                type="password"
                required
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="py-2.5"
              />
              <span className="text-xs text-ink-subtle">
                {MIN_PASSWORD_LENGTH} 文字以上。長さがそのまま強度になります。
              </span>
            </label>

            <label className="flex flex-col gap-2 text-sm">
              確認のためもう一度
              <Input
                type="password"
                required
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="py-2.5"
              />
              {mismatch ? <span className="text-xs text-alert">一致していません。</span> : null}
            </label>

            {failure ? <p className="text-sm text-alert">{failure}</p> : null}

            <Button type="submit" variant="primary" size="md" disabled={busy || !ready}>
              {busy ? '設定中…' : '設定して始める'}
            </Button>
          </form>
        ) : null}
      </div>
    </div>
  );
}
